import { Box, Typography, Button, Paper, Alert } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import StarIcon from '@mui/icons-material/Star';
import LockIcon from '@mui/icons-material/Lock';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuthStore } from '../store/auth.store';
import { Role } from '../types/auth.type';
import { isPremiumOrg } from '../utils/organizationServiceStatus.utils';

interface FreemiumProtectedRouteProps {
  children: React.ReactNode;
  feature: 'reports' | 'unlimited_uploads' | 'add_reports';
}

const FreemiumProtectedRoute = ({ children, feature }: FreemiumProtectedRouteProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { organizationId } = useParams<{ organizationId: string }>();
  const { user } = useAuthStore();

  const isPremium = user?.organization ? isPremiumOrg(user.organization) : false;
  const isOrganizationUser = user?.data.role === Role.ORGANIZATION || user?.data.role === Role.ORGANIZATION_USER;
  const showRestriction = isOrganizationUser && !isPremium;

  if (!showRestriction) return <>{children}</>;

  const handleSubscribe = () => {
    if (organizationId) {
      navigate(`/dashboard/organizations/${organizationId}/subscription/plans`);
    } else {
      window.open('https://mohasby.com', '_blank');
    }
  };

  const getFeatureMessage = () => {
    switch (feature) {
      case 'reports':
        return { title: t('premium.reportsLocked'), message: t('premium.reportsLockedMessage') };
      case 'add_reports':
        return { title: t('premium.addReportsLocked'), message: t('premium.addReportsLockedMessage') };
      case 'unlimited_uploads':
        return { title: t('premium.uploadsLocked'), message: t('premium.uploadsLockedMessage') };
      default:
        return { title: t('premium.featureLocked'), message: t('premium.featureLockedMessage') };
    }
  };

  const { title, message } = getFeatureMessage();

  return (
    <Box sx={{ p: 4, textAlign: 'center' }}>
      <Paper
        elevation={0}
        sx={{
          p: 4,
          maxWidth: 500,
          mx: 'auto',
          borderRadius: 3,
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box
          sx={{
            width: 64, height: 64, borderRadius: '50%',
            bgcolor: alpha(theme.palette.warning.main, 0.1),
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            mx: 'auto', mb: 2.5,
          }}
        >
          <LockIcon sx={{ fontSize: 32, color: theme.palette.warning.main }} />
        </Box>

        <Typography variant="h5" sx={{ mb: 1.5, fontWeight: 700 }}>
          {title}
        </Typography>

        <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
          {message}
        </Typography>

        <Alert severity="info" sx={{ mb: 3, borderRadius: 2, textAlign: 'start' }}>
          <Typography variant="body2">{t('premium.upgradeBenefits')}</Typography>
        </Alert>

        <Button
          variant="contained"
          size="large"
          onClick={handleSubscribe}
          startIcon={<StarIcon sx={{ fontSize: 20 }} />}
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            px: 4,
            py: 1.5,
            borderRadius: 2.5,
            fontSize: '0.95rem',
          }}
        >
          {t('subscription.viewPlans')}
        </Button>
      </Paper>
    </Box>
  );
};

export default FreemiumProtectedRoute;
